import { useCallback, useEffect, useState } from 'react'
import { api, ApiError } from '../api/client.js'
import { useAuth } from '../auth/AuthContext.js'

type SubView = 'broker-worklist' | 'vendors' | 'ageing'

const SUB_NAV: Array<{ key: SubView; label: string }> = [
  { key: 'broker-worklist', label: 'Broker Payables' },
  { key: 'vendors', label: 'Vendor Payments' },
  { key: 'ageing', label: 'Payables Ageing' },
]

/**
 * Stage 9: broker hire-cost/brokerage worklist and settlement, vendor
 * payments, and combined payables ageing with Payment Advice. Driver
 * settlement lives on the Trip Detail screen instead (Stage 9 §4's
 * GET/POST /api/trips/:id/driver-settlement is trip-scoped, not a
 * standalone worklist), matching where POD/E-Way Bill live for the same reason.
 */
export function PayablesPage() {
  const [sub, setSub] = useState<SubView>('broker-worklist')

  return (
    <div>
      <nav className="tf-tabs" style={{ marginBottom: '1rem' }}>
        {SUB_NAV.map((item) => (
          <button
            key={item.key}
            type="button"
            className={sub === item.key ? 'tf-tab-active' : ''}
            onClick={() => setSub(item.key)}
          >
            {item.label}
          </button>
        ))}
      </nav>

      {sub === 'broker-worklist' && <BrokerWorklistTab />}
      {sub === 'vendors' && <VendorPaymentsTab />}
      {sub === 'ageing' && <AgeingTab />}
    </div>
  )
}

// ---------------------------------------------------------------------------
interface WorklistRow {
  source_id: string
  source_type: 'HireCost' | 'Brokerage'
  broker_id: string
  broker_name: string
  trip_id: string
  trip_no: string
  amount: string
  balance: string
  ready: boolean
}

function BrokerWorklistTab() {
  const [rows, setRows] = useState<WorklistRow[]>([])
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [mode, setMode] = useState<'Cash' | 'Bank' | 'UPI' | 'Cheque'>('Bank')
  const [referenceNo, setReferenceNo] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const key = (r: WorklistRow) => `${r.source_type}:${r.source_id}`

  const reload = useCallback(async () => {
    const res = await api.get<WorklistRow[]>('/brokers/payables-worklist')
    setRows(res)
    setSelected(new Set())
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  const byBroker = new Map<string, WorklistRow[]>()
  for (const row of rows) {
    const list = byBroker.get(row.broker_id) ?? []
    list.push(row)
    byBroker.set(row.broker_id, list)
  }

  function toggle(k: string) {
    setSelected((prev) => {
      const next = new Set(prev)
      if (next.has(k)) next.delete(k)
      else next.add(k)
      return next
    })
  }

  async function recordPayment(brokerId: string, brokerRows: WorklistRow[]) {
    const lines = brokerRows.filter((r) => selected.has(key(r)))
    if (!lines.length) return
    const amount = lines.reduce((sum, r) => sum + Number(r.balance), 0)
    setSaving(true)
    setError(null)
    try {
      await api.post('/broker-payments', {
        broker_id: brokerId,
        amount,
        mode,
        reference_no: referenceNo || undefined,
        lines: lines.map((r) => ({ source_type: r.source_type, source_id: r.source_id, amount: Number(r.balance) })),
      })
      await reload()
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to record payment')
    } finally {
      setSaving(false)
    }
  }

  if (!rows.length) return <p className="tf-note">No hire cost or brokerage is currently outstanding.</p>

  return (
    <div>
      {error && <p className="tf-error">{error}</p>}
      {[...byBroker.entries()].map(([brokerId, brokerRows]) => {
        const selectedHere = brokerRows.filter((r) => selected.has(key(r)))
        return (
          <div key={brokerId} className="tf-card" style={{ marginBottom: '1rem' }}>
            <h3>{brokerRows[0]!.broker_name}</h3>
            <table className="tf-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Trip</th>
                  <th>Type</th>
                  <th>Balance</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {brokerRows.map((r) => (
                  <tr key={key(r)}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selected.has(key(r))}
                        disabled={!r.ready}
                        onChange={() => toggle(key(r))}
                      />
                    </td>
                    <td>{r.trip_no}</td>
                    <td>{r.source_type === 'HireCost' ? 'Hire Cost' : 'Brokerage'}</td>
                    <td>₹{r.balance}</td>
                    <td>{r.ready ? 'Ready' : 'Pending POD'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {selectedHere.length > 0 && (
              <div className="tf-form" style={{ marginTop: '0.75rem' }}>
                <label className="tf-field">
                  <span>Mode</span>
                  <select value={mode} onChange={(e) => setMode(e.target.value as typeof mode)}>
                    <option value="Bank">Bank</option>
                    <option value="Cash">Cash</option>
                    <option value="UPI">UPI</option>
                    <option value="Cheque">Cheque</option>
                  </select>
                </label>
                <label className="tf-field">
                  <span>Reference no. (optional)</span>
                  <input value={referenceNo} onChange={(e) => setReferenceNo(e.target.value)} />
                </label>
                <div className="tf-form-actions">
                  <button
                    type="button"
                    className="tf-button-primary"
                    disabled={saving}
                    onClick={() => void recordPayment(brokerId, brokerRows)}
                  >
                    {saving ? 'Recording…' : `Record Payment — ₹${selectedHere.reduce((s, r) => s + Number(r.balance), 0)}`}
                  </button>
                </div>
              </div>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ---------------------------------------------------------------------------
function VendorPaymentsTab() {
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([])
  const [vendorId, setVendorId] = useState('')
  const [amount, setAmount] = useState('')
  const [tripId, setTripId] = useState('')
  const [remarks, setRemarks] = useState('')
  const [status, setStatus] = useState<'Pending' | 'Paid'>('Paid')
  const [payments, setPayments] = useState<any[]>([])
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    void api.get<{ rows: { id: string; name: string }[] }>('/vendors').then((res) => setVendors(res.rows))
  }, [])

  const loadPayments = useCallback(async (id: string) => {
    if (!id) return setPayments([])
    setPayments(await api.get(`/vendors/${id}/payments`))
  }, [])

  useEffect(() => {
    void loadPayments(vendorId)
  }, [vendorId, loadPayments])

  async function submit() {
    if (!vendorId) return
    setSaving(true)
    setError(null)
    try {
      await api.post(`/vendors/${vendorId}/payments`, {
        amount,
        trip_id: tripId || undefined,
        remarks: remarks || undefined,
        status,
      })
      setAmount('')
      setTripId('')
      setRemarks('')
      await loadPayments(vendorId)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save payment')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      <label className="tf-field" style={{ maxWidth: '20rem' }}>
        <span>Vendor</span>
        <select value={vendorId} onChange={(e) => setVendorId(e.target.value)}>
          <option value="">Select a vendor…</option>
          {vendors.map((v) => (
            <option key={v.id} value={v.id}>
              {v.name}
            </option>
          ))}
        </select>
      </label>

      {vendorId && (
        <>
          <div className="tf-form" style={{ margin: '1rem 0' }}>
            <label className="tf-field">
              <span>Amount</span>
              <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} />
            </label>
            <label className="tf-field">
              <span>Trip ID (optional — leave blank for a periodic payment)</span>
              <input value={tripId} onChange={(e) => setTripId(e.target.value)} />
            </label>
            <label className="tf-field">
              <span>Remarks {!tripId && '(required for a periodic payment)'}</span>
              <input value={remarks} onChange={(e) => setRemarks(e.target.value)} />
            </label>
            <label className="tf-field">
              <span>Status</span>
              <select value={status} onChange={(e) => setStatus(e.target.value as typeof status)}>
                <option value="Paid">Paid</option>
                <option value="Pending">Pending (scheduled, not yet paid)</option>
              </select>
            </label>
            {error && <p className="tf-error">{error}</p>}
            <div className="tf-form-actions">
              <button type="button" className="tf-button-primary" disabled={saving} onClick={() => void submit()}>
                {saving ? 'Saving…' : 'Save Payment'}
              </button>
            </div>
          </div>

          <table className="tf-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Amount</th>
                <th>Trip</th>
                <th>Status</th>
                <th>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {payments.map((p) => (
                <tr key={p.id}>
                  <td>{new Date(p.payment_date).toLocaleDateString()}</td>
                  <td>₹{p.amount}</td>
                  <td>{p.trip_no ?? '—'}</td>
                  <td>{p.status}</td>
                  <td>{p.remarks ?? '—'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  )
}

// ---------------------------------------------------------------------------
interface AgeingRow {
  counterparty_type: string
  counterparty_id: string
  counterparty_name: string
  outstanding: string
  bucket_0_30: string
  bucket_31_60: string
  bucket_61_90: string
  bucket_90_plus: string
}

function AgeingTab() {
  const { currentBranchId } = useAuth()
  const [rows, setRows] = useState<AgeingRow[]>([])
  const [adviceFor, setAdviceFor] = useState<AgeingRow | null>(null)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [savedAdviceNo, setSavedAdviceNo] = useState<string | null>(null)

  const reload = useCallback(async () => {
    setRows(await api.get<AgeingRow[]>('/payables/ageing'))
  }, [])

  useEffect(() => {
    void reload()
  }, [reload])

  async function createAdvice() {
    if (!adviceFor || !currentBranchId) return
    setSaving(true)
    setError(null)
    try {
      const body: Record<string, unknown> = {
        branch_id: currentBranchId,
        lines: [{ description: adviceFor.counterparty_type, amount: adviceFor.outstanding }],
      }
      if (adviceFor.counterparty_type === 'Vendor') body.vendor_id = adviceFor.counterparty_id
      else if (adviceFor.counterparty_type.startsWith('Broker')) body.broker_id = adviceFor.counterparty_id
      else body.driver_id = adviceFor.counterparty_id

      const advice = await api.post<{ advice_no: string }>('/payment-advices', body)
      setSavedAdviceNo(advice.advice_no)
      setAdviceFor(null)
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not create payment advice')
    } finally {
      setSaving(false)
    }
  }

  return (
    <div>
      {savedAdviceNo && <p className="tf-note">Payment advice {savedAdviceNo} created.</p>}
      <table className="tf-table">
        <thead>
          <tr>
            <th>Type</th>
            <th>Name</th>
            <th>Outstanding</th>
            <th>0–30</th>
            <th>31–60</th>
            <th>61–90</th>
            <th>90+</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={`${r.counterparty_type}:${r.counterparty_id}`}>
              <td>{r.counterparty_type}</td>
              <td>{r.counterparty_name}</td>
              <td>₹{r.outstanding}</td>
              <td>₹{r.bucket_0_30}</td>
              <td>₹{r.bucket_31_60}</td>
              <td>₹{r.bucket_61_90}</td>
              <td>₹{r.bucket_90_plus}</td>
              <td>
                <button type="button" onClick={() => setAdviceFor(r)}>
                  Payment Advice
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {adviceFor && (
        <div className="tf-slideover-backdrop" onClick={() => setAdviceFor(null)}>
          <div className="tf-slideover" onClick={(e) => e.stopPropagation()}>
            <h2>Payment Advice — {adviceFor.counterparty_name}</h2>
            <p className="tf-note">Amount: ₹{adviceFor.outstanding}</p>
            {error && <p className="tf-error">{error}</p>}
            <div className="tf-form-actions">
              <button type="button" className="tf-button-primary" disabled={saving} onClick={() => void createAdvice()}>
                {saving ? 'Creating…' : 'Create Advice'}
              </button>
              <button type="button" onClick={() => setAdviceFor(null)}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
